import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Profile } from '@lr/shared';
import { apiFetch } from '../../lib/api';

type Row = Profile & { orders?: { count: number }[] | null };
type RoleFilter = 'customers' | 'admins' | 'all';

export function AdminCustomers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('customers');

  useEffect(() => {
    apiFetch<Row[]>('/api/admin/users')
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    let customers = 0;
    let admins = 0;
    for (const u of rows) {
      if (u.role === 'user') customers++;
      else admins++;
    }
    return { customers, admins, all: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((u) => {
      if (roleFilter === 'customers' && u.role !== 'user') return false;
      if (roleFilter === 'admins' && u.role === 'user') return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) ||
        `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase().includes(q) ||
        (u.phone ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, roleFilter]);

  return (
    <>
      <div className="a-tabs">
        <button type="button" className="a-tab active">
          Customers
        </button>
        <Link to="/admin/id-verifications" className="a-tab">
          ID Verifications
        </Link>
      </div>
      <div className="admin-page-head">
        <div>
          <h1>Customers</h1>
          <div className="sub">
            {counts.customers} {counts.customers === 1 ? 'customer' : 'customers'}, {counts.admins} admin
            {counts.admins === 1 ? '' : 's'}.
          </div>
        </div>
      </div>

      <div className="a-card">
        <div className="a-toolbar">
          <div className="a-search">
            <span className="ic">
              <SearchIcon />
            </span>
            <input
              placeholder="Search by name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {(
            [
              ['customers', 'Customers', counts.customers],
              ['admins', 'Admins', counts.admins],
              ['all', 'All', counts.all]
            ] as [RoleFilter, string, number][]
          ).map(([k, label, count]) => (
            <button
              key={k}
              className={`a-filter${roleFilter === k ? ' active' : ''}`}
              onClick={() => setRoleFilter(k)}
            >
              {label}
              <span style={{ opacity: 0.7, marginLeft: 4 }}>{count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="a-empty">
            <div className="h">
              {rows.length === 0
                ? 'No users yet'
                : roleFilter === 'customers'
                  ? 'No regular customers yet'
                  : roleFilter === 'admins'
                    ? 'No admins'
                    : 'No matches'}
            </div>
            <div className="p">
              {rows.length === 0
                ? 'Users appear here as soon as they sign up.'
                : roleFilter === 'customers'
                  ? 'Everyone signed up so far is an admin. Switch to "All" to see them.'
                  : 'Try a different search or switch the filter.'}
            </div>
            {rows.length > 0 && roleFilter !== 'all' && (
              <button
                className="btn-primary"
                onClick={() => setRoleFilter('all')}
                style={{ marginTop: 12 }}
              >
                Show all users
              </button>
            )}
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ width: 120 }}>Role</th>
                  <th>Phone</th>
                  <th style={{ width: 110 }}>Credits</th>
                  <th style={{ width: 90 }}>Orders</th>
                  <th style={{ width: 130 }}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    className="clickable"
                    onClick={() => navigate(`/admin/customers/${u.id}`)}
                  >
                    <td>
                      <div className="stack">
                        <div className="thumb" style={{ background: 'var(--a-ink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', fontSize: 12, fontWeight: 700 }}>
                          {(u.first_name ?? u.email).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="name">
                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                          </div>
                          <div className="sub id">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${u.role === 'super_admin' ? 'badge-dark' : u.role === 'admin' ? 'badge-info' : 'badge-neutral'}`}>
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{u.phone ?? '—'}</td>
                    <td className="num">{u.credit_balance}</td>
                    <td className="num">{u.orders?.[0]?.count ?? 0}</td>
                    <td>{new Date(u.created_at).toLocaleDateString('en-GB')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
